import React, { useState, useEffect, useMemo } from "react";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { useRoles } from "@/hooks/useRoles";
import { computeParentChain } from "@/utils/personnelHierarchy";
import { isSystemAdminPersonnel } from "@/utils/roleUtils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Calendar, Clock, CheckCircle, Package, Briefcase, FileText,
  Users, TreePine, Phone, Mail, BarChart3, MapPin, X, Edit, Save, RefreshCw, ChevronDown, UserCheck, Lock, Sparkles
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SupervisorPicker from "@/components/personnel/SupervisorPicker";
import TeamPicker from "@/components/personnel/TeamPicker";
import axios from "axios";
import { BACKEND_URL } from "@/config";
import { formatTrCepDisplay, formatTrCepStoredDisplay, normalizeTrCepToLocal11 } from "@/utils/phoneFormat";
import { canonicalizeReasonType, computeSocialUsedDays } from "@/utils/leaveUtils";

const API = BACKEND_URL;
const GOLGE_BASKAN_ID = "golge_admin_baskan";

// Vekalet Sekmesi Komponenti
const SubstitutionTab = ({ personnel }) => {
  const [substitutionState, setSubstitutionState] = useState({ as_substitute: [], on_leave: [] });
  const [loading, setLoading] = useState(true);
  const getParkNames = (sub) => {
    const parks = Array.isArray(sub?.parks) ? sub.parks : [];
    return parks
      .map((p) => (typeof p === "string" ? p : (p?.name || p?.park_name || "")))
      .filter(Boolean);
  };

  useEffect(() => {
    if (personnel?.id) {
      fetchSubstitutionHistory();
    }
  }, [personnel?.id]);

  const fetchSubstitutionHistory = async () => {
    setLoading(true);
    try {
      // Personelin "İzin Yönetimi" ekranıyla aynı endpoint.
      const response = await axios.get(`${API}/api/active-substitutions/personnel/${personnel.id}`);
      setSubstitutionState(response.data || { as_substitute: [], on_leave: [] });
    } catch (error) {
      console.error("Vekalet geçmişi yüklenirken hata:", error);
      setSubstitutionState({ as_substitute: [], on_leave: [] });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Vekalet geçmişi yükleniyor...</span>
      </div>
    );
  }

  const asSubstitute = substitutionState.as_substitute || [];
  const onLeave = substitutionState.on_leave || [];

  return (
    <div className="space-y-3">
      <details className="group">
        <summary className="cursor-pointer list-none">
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-500 rounded-full p-2">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900">🤝 Vekalet Aldığım</h3>
                    <p className="text-xs text-amber-700">
                      {asSubstitute.length > 0 ? `${asSubstitute.length} kayıt` : "Aktif vekalet yok"}
                    </p>
                  </div>
                </div>
                <ChevronDown className="h-5 w-5 text-amber-600 group-open:rotate-180 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </summary>
        <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          {asSubstitute.length === 0 ? (
            <p className="text-sm text-amber-800">Aktif vekalet yok.</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {asSubstitute.map((sub) => (
                <div key={sub.id} className="bg-white rounded border border-amber-200 p-2 text-sm">
                  <div className="font-semibold text-gray-800">{sub.original_personnel_name}</div>
                  <div className="text-xs text-gray-600">
                    {new Date(sub.start_date).toLocaleDateString("tr-TR")} - {new Date(sub.end_date).toLocaleDateString("tr-TR")}
                  </div>
                  {sub.parks?.length > 0 && (
                    <div className="text-[11px] text-gray-500 mt-1">{sub.parks.length} park</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </details>

      <details className="group">
        <summary className="cursor-pointer list-none">
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <UserCheck className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-900">🧾 Vekalet Verdiğim</h3>
                    <p className="text-xs text-green-700">
                      {onLeave.length > 0 ? `${onLeave.length} kayıt` : "Aktif vekalet yok"}
                    </p>
                  </div>
                </div>
                <ChevronDown className="h-5 w-5 text-green-600 group-open:rotate-180 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </summary>
        <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
          {onLeave.length === 0 ? (
            <p className="text-sm text-green-800">Aktif vekalet yok.</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {onLeave.map((sub) => (
                <div key={sub.id} className="bg-white rounded border border-green-200 p-2 text-sm">
                  <div className="font-semibold text-gray-800">{sub.substitute_personnel_name}</div>
                  <div className="text-xs text-gray-600">
                    {new Date(sub.start_date).toLocaleDateString("tr-TR")} - {new Date(sub.end_date).toLocaleDateString("tr-TR")}
                  </div>
                  {(sub.parks || []).length > 0 && (
                    <div className="text-[11px] text-green-700 mt-1 space-y-0.5">
                      {(sub.parks || []).map((p, idx) => {
                        const parkName = typeof p === "string" ? p : (p?.name || p?.park_name || "");
                        // Çöktürülmüş kayıtta üst-seviye substitute_personnel_name yalnız
                        // "ilk dolu vekil"dir. Bu parkın gerçek vekilini per-park kırılımdan
                        // (substitute_breakdown) çöz; yoksa park-entry alanlarına, en son
                        // üst-seviyeye düş.
                        const pid = typeof p === "string" ? null : (p?.id || p?.park_id);
                        const breakdown = Array.isArray(sub?.substitute_breakdown) ? sub.substitute_breakdown : [];
                        const be = pid ? breakdown.find((b) => (b?.id || b?.park_id) === pid) : null;
                        const vekil = (be?.substitute_personnel_name || p?.substitute_name || p?.substitute_personnel_name || sub?.substitute_personnel_name || "").trim();
                        return parkName ? <div key={idx}>🌳 {parkName} → {vekil || "Vekil"}</div> : null;
                      })}
                    </div>
                  )}
                  {sub.reason_type && <div className="text-[11px] text-gray-500 mt-1">{sub.reason_type}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default function PersonnelDetailsDialog({
  isOpen,
  onClose,
  personnel,
  leaveRequests,
  materialRequests,
  getRoleBadge,
  allPersonnel = [],
  parks = [],
  onPersonnelUpdated,
  onOpenAdvancedEdit,
  defaultTab = "overview",
  userRole = "",
  currentPersonnelId = "",
}) {
  const [activeTab, setActiveTab] = useState(defaultTab || "overview");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("all");
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  // Özel Rol Ata dialog state'i
  const [customRoleDialogOpen, setCustomRoleDialogOpen] = useState(false);
  const [customRoleDraft, setCustomRoleDraft] = useState({ name: '', description: '' });
  const roleLower = (userRole || "").toLowerCase();
  const canEditRole = roleLower === 'mudur' || roleLower === 'admin';
  const targetIsSuperAdminRole = (personnel?.role || "").toLowerCase() === "admin";
  const canSetPassword = Boolean(personnel?.id) && (
    String(currentPersonnelId || "") === String(personnel?.id || "") ||
    (roleLower === "mudur" && !targetIsSuperAdminRole) ||
    (roleLower === "admin" && !targetIsSuperAdminRole) ||
    (roleLower === "golge_admin" && String(currentPersonnelId || "") === GOLGE_BASKAN_ID && !targetIsSuperAdminRole)
  );
  const showSetPasswordButton = canSetPassword;
  const canEditPhone = roleLower === "mudur" || roleLower === "admin";
  
  // Hiyerarşi: dinamik amir zinciri (parent_personnel_id → yukarı).
  // Hook'lar erken return'den önce çağrılmalı (Rules of Hooks).
  // Rol referans listesi yalnız diyalog açıkken çekilir — kapalıyken (bu diyalog
  // her rolde mount olur) `/admin/roles` çağrılmaz; yetkisiz rolde 403 + console
  // hatası önlenir (Kapı 11 console/network gate).
  const { roles, hierarchyRoles, getRoleById, getRoleBySlug, getRoleLabel } = useRoles({ onlyActive: true, enabled: isOpen });

  // Reset form when personnel changes — tüm DB alanları otomatik dolar
  useEffect(() => {
    if (personnel) {
      const resolvedRoleId =
        personnel.role_id || (personnel.role ? (getRoleBySlug(personnel.role)?.id || '') : '');
      setEditForm({
        name: personnel.name || '',
        phone: normalizeTrCepToLocal11(personnel.phone || ''),
        email: personnel.email || '',
        custom_role_name: personnel.custom_role_name || '',
        custom_role_description: personnel.custom_role_description || '',
        primary_team_id: personnel.primary_team_id || '',
        secondary_team_id: personnel.secondary_team_id || '',
        role: personnel.role || '',
        role_id: resolvedRoleId,
        parent_personnel_id: personnel.parent_personnel_id || '',
        assignment_type: personnel.assignment_type || 'NORMAL',
        hire_date: personnel.hire_date || '',
        annual_leave_days: personnel.annual_leave_days ?? 20,
        used_leave_days: personnel.used_leave_days ?? 0,
        status: personnel.status || 'active',
      });
      setIsEditing(false);
      setActiveTab(defaultTab || "overview");
    }
  }, [personnel, defaultTab, getRoleBySlug]);

  const parentChain = useMemo(
    () => computeParentChain(personnel, allPersonnel, { getRoleById, getRoleBySlug }),
    [personnel, allPersonnel, getRoleById, getRoleBySlug]
  );

  // Rol/Hiyerarşi inline edit için PersonnelPage ile aynı mantık
  const selectedRoleDef = useMemo(
    () => (editForm.role_id ? getRoleById(editForm.role_id) : getRoleBySlug(editForm.role)),
    [editForm.role_id, editForm.role, getRoleById, getRoleBySlug]
  );
  const eligibleSupervisors = useMemo(() => {
    if (!selectedRoleDef) return [];
    const myLevel = Number(selectedRoleDef.hierarchy_level ?? 0);
    return (allPersonnel || [])
      .filter((p) => {
        if (p.status !== 'active') return false;
        if (p.id === personnel?.id) return false;
        if (isSystemAdminPersonnel(p, { getRoleById })) return false;
        const pRole = p.role_id ? getRoleById(p.role_id) : getRoleBySlug(p.role);
        if (!pRole) return false;
        return Number(pRole.hierarchy_level ?? 0) > myLevel;
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr'));
  }, [allPersonnel, selectedRoleDef, personnel?.id, getRoleById, getRoleBySlug]);
  const maxHierarchyLevel = useMemo(
    () => hierarchyRoles.reduce((max, r) => Math.max(max, Number(r?.hierarchy_level ?? 0)), 0),
    [hierarchyRoles]
  );
  const supervisorMode = useMemo(() => {
    if (!selectedRoleDef) return 'no-role';
    if (Number(selectedRoleDef.hierarchy_level ?? 0) >= maxHierarchyLevel) return 'root-role';
    return 'ready';
  }, [selectedRoleDef, maxHierarchyLevel]);
  const supervisorPlaceholder = {
    'no-role': 'Önce rol seçin',
    'root-role': 'Bu rol hiyerarşinin kökü — üst amir yok',
    'ready': 'Amir seçin',
  }[supervisorMode];
  const supervisorHint = {
    'no-role': '⚠️ Bu alan için önce yukarıdaki Rol alanını seçmelisiniz.',
    'root-role': 'ℹ️ Seçili rol hiyerarşinin en üstündedir; bağlanacak üst amir yoktur.',
    'ready': null,
  }[supervisorMode];

  if (!personnel) return null;

  const personnelLeaves = leaveRequests.filter(lr => lr.personnel_id === personnel.id);
  const personnelMaterials = materialRequests.filter(mr => mr.requester_id === personnel.id);

  // Sorumlu parkları bul - birden fazla yöntemle eşleştir
  const assignedParks = parks.filter(park => {
    if (personnel.assigned_park_ids?.includes(park.id)) return true;
    if (park.assigned_personnel?.includes(personnel.id)) return true;
    if (park.assigned_personnel_names?.includes(personnel.name)) return true;
    return false;
  });

  // Group leaves by type — reason_type slug/varyant olabileceği için kanonikleştir
  // (aksi halde slug'lı talepler gösterimden düşer ama sayaca dahil olur).
  const groupedLeaves = personnelLeaves.reduce((acc, leave) => {
    const key = canonicalizeReasonType(leave.reason_type);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(leave);
    return acc;
  }, {});

  const leaveTypeOrder = ["Yıllık İzin", "Sosyal İzin", "Rapor", "Diğer"];
  const leaveTypes = [
    ...leaveTypeOrder.filter(type => groupedLeaves[type]),
    ...Object.keys(groupedLeaves).filter(type => !leaveTypeOrder.includes(type)),
  ];

  // Kullanılan sosyal izin — LeaveManagementPanel ile birebir aynı türetme
  // (stale sayaç yerine onaylı taleplerden max), yüzeyler arası tutarlılık için.
  const socialUsedDays = computeSocialUsedDays(personnel, personnelLeaves);

  // Group materials by type
  const groupedMaterials = personnelMaterials.reduce((acc, mat) => {
    if (!acc[mat.material_type]) {
      acc[mat.material_type] = [];
    }
    acc[mat.material_type].push(mat);
    return acc;
  }, {});

  const materialTypes = Object.keys(groupedMaterials).sort();

  // İstatistikler
  const currentYear = new Date().getFullYear();
  const thisYearLeaves = personnelLeaves.filter(l => new Date(l.created_at).getFullYear() === currentYear);
  const thisYearRaporDays = thisYearLeaves
    .filter(l => l.reason_type === "Rapor" && l.status === "approved")
    .reduce((sum, l) => {
      const start = new Date(l.start_date);
      const end = new Date(l.end_date);
      return sum + Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }, 0);
  const thisYearOtherCount = thisYearLeaves.filter(l => l.reason_type === "Diğer").length;
  
  const thisYearMaterialCount = personnelMaterials.filter(m => 
    new Date(m.created_at).getFullYear() === currentYear
  ).length;

  const totalMaterialsReceived = personnelMaterials
    .filter(m => m.status === 'delivered')
    .reduce((sum, m) => sum + m.quantity, 0);

  const getLeaveTypeStyle = (type) => {
    switch(type) {
      case "Yıllık İzin": return { emoji: "🏖️", color: "from-blue-50 to-blue-100", border: "border-blue-300" };
      case "Sosyal İzin": return { emoji: "👨‍👩‍👧‍👦", color: "from-purple-50 to-purple-100", border: "border-purple-300" };
      case "Rapor": return { emoji: "🏥", color: "from-red-50 to-red-100", border: "border-red-300" };
      default: return { emoji: "📋", color: "from-gray-50 to-gray-100", border: "border-gray-300" };
    }
  };

  // İzin türüne göre kalan gün hesaplama
  const getLeaveRemaining = (type) => {
    switch(type) {
      case "Yıllık İzin": 
        const annualTotal = personnel.annual_leave_days || 20;
        const annualUsed = personnel.used_leave_days || 0;
        return { remaining: annualTotal - annualUsed, total: annualTotal };
      case "Sosyal İzin":
        return { remaining: 12 - socialUsedDays, total: 12 };
      case "Rapor": 
        const reportUsed = personnel.used_report_days || 0;
        return { remaining: 40 - reportUsed, total: 40 };
      default: 
        return { remaining: null, total: null };
    }
  };

  const getMaterialIcon = (type) => {
    const icons = {
      'Tırmık': '🔱', 'Yelpaze Tırmık': '🌀', 'Kürek': '⛏️', 'Çalı Süpürge': '🧹',
      'Faraş': '🗑️', 'Budama Makası': '✂️', 'Bahçe Hortumu': '💧', 'Eldiven': '🧤',
      'Çöp Torbası': '🛍️', 'Diğer': '📦'
    };
    return icons[type] || '📦';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Eski `position` alanı backend'den kalktığı için spread'ten çıkar
      const { position: _drop, ...personnelRest } = personnel;
      const parentForSave = supervisorMode === 'ready' ? (editForm.parent_personnel_id || null) : null;
      await axios.put(`${API}/api/personnel/${personnel.id}`, {
        ...personnelRest,
        name: editForm.name,
        phone: editForm.phone,
        email: editForm.email,
        custom_role_name: editForm.custom_role_name?.trim() || null,
        custom_role_description: editForm.custom_role_description?.trim() || null,
        primary_team_id: editForm.primary_team_id || null,
        secondary_team_id: editForm.secondary_team_id || null,
        role: editForm.role,
        role_id: editForm.role_id || null,
        parent_personnel_id: parentForSave,
        assignment_type: parentForSave ? (editForm.assignment_type || 'NORMAL') : 'NORMAL',
        hire_date: editForm.hire_date || null,
        annual_leave_days: editForm.annual_leave_days,
        used_leave_days: editForm.used_leave_days,
        status: editForm.status
      });
      setIsEditing(false);
      if (onPersonnelUpdated) {
        onPersonnelUpdated();
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      alert('Güncelleme sırasında bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    setActiveTab("overview");
    setSetPasswordOpen(false);
    setNewPassword("");
    setNewPasswordConfirm("");
    onClose(false);
  };

  const handleSetPassword = async () => {
    if (!personnel?.id || !newPassword.trim() || newPassword.length < 4) {
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      alert("Şifre alanları eşleşmiyor.");
      return;
    }
    setSetPasswordLoading(true);
    try {
      await axios.put(`${API}/api/personnel/${personnel.id}/set-password`, { new_password: newPassword });
      setSetPasswordOpen(false);
      setNewPassword("");
      setNewPasswordConfirm("");
      if (onPersonnelUpdated) onPersonnelUpdated();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === "string" ? d : (Array.isArray(d) ? d.join(" ") : "Şifre güncellenemedi"));
    } finally {
      setSetPasswordLoading(false);
    }
  };

  return (
  <>
    <DraggableModal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
            {personnel.name?.charAt(0)}
          </div>
          <div>
            <span className="text-lg font-bold">{personnel.name}</span>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`text-xs font-semibold ${getRoleBadge(personnel.role).className}`}>
                {getRoleBadge(personnel.role).label}
              </Badge>
              {personnel.status === 'active' ? (
                <Badge className="bg-green-500 text-xs">Aktif</Badge>
              ) : (
                <Badge className="bg-gray-400 text-xs">Pasif</Badge>
              )}
            </div>
          </div>
        </div>
      }
      subtitle={
        <div className="flex items-center gap-4 flex-wrap text-sm text-gray-600">
          {personnel.phone && (
            <span className="flex items-center gap-1">
              <Phone className="h-4 w-4" /> {formatTrCepStoredDisplay(personnel.phone)}
            </span>
          )}
          {personnel.email && (
            <span className="flex items-center gap-1">
              <Mail className="h-4 w-4" /> {personnel.email}
            </span>
          )}
          {personnel.hire_date && (
            <span className="flex items-center gap-1">
              <Briefcase className="h-4 w-4" /> 
              {new Date(personnel.hire_date).toLocaleDateString('tr-TR')} ({Math.floor((new Date() - new Date(personnel.hire_date)) / (1000 * 60 * 60 * 24 * 365))} yıl)
            </span>
          )}
        </div>
      }
      maxWidth="max-w-5xl"
    >
        <div className="max-h-[70vh] overflow-y-auto">
          {showSetPasswordButton && (
            <div className="mb-4 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setSetPasswordOpen(true)}
              >
                <Lock className="h-4 w-4" /> Şifre ata / değiştir
              </Button>
            </div>
          )}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-6 mb-4">
              <TabsTrigger value="overview" className="flex items-center gap-1">
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">Özet</span>
              </TabsTrigger>
              <TabsTrigger value="leaves" className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">İzinler</span>
                {personnelLeaves.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">{personnelLeaves.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="materials" className="flex items-center gap-1">
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">Malzemeler</span>
                {personnelMaterials.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">{personnelMaterials.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="parks" className="flex items-center gap-1">
                <TreePine className="h-4 w-4" />
                <span className="hidden sm:inline">Parklar</span>
                {assignedParks.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">{assignedParks.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="substitutions" className="flex items-center gap-1 text-indigo-600">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Vekalet</span>
              </TabsTrigger>
              <TabsTrigger value="edit" className="flex items-center gap-1 text-blue-600">
                <Edit className="h-4 w-4" />
                <span className="hidden sm:inline">Düzenle</span>
              </TabsTrigger>
            </TabsList>

            {/* ÖZET SEKMESİ */}
            <TabsContent value="overview" className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                {/* İşe Giriş Tarihi */}
                <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="h-4 w-4 text-blue-600" />
                    <h4 className="font-semibold text-sm text-blue-800">İşe Giriş Tarihi</h4>
                  </div>
                  {personnel.hire_date ? (
                    <p className="text-lg font-bold text-blue-900">
                      {new Date(personnel.hire_date).toLocaleDateString('tr-TR')}
                    </p>
                  ) : (
                    <p className="text-sm text-blue-600">Henüz girilmemiş</p>
                  )}
                </div>

                {/* Yıllık İzin Durumu */}
                <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-green-600" />
                    <h4 className="font-semibold text-sm text-green-800">🏖️ Yıllık İzin</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${
                      ((personnel.annual_leave_days || 20) - (personnel.used_leave_days || 0)) <= 5 
                        ? 'text-red-600' 
                        : 'text-green-700'
                    }`}>
                      {(personnel.annual_leave_days || 20) - (personnel.used_leave_days || 0)}
                    </span>
                    <span className="text-xs text-green-600">gün kalan</span>
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1">
                    {personnel.annual_leave_days || 20} toplam • {personnel.used_leave_days || 0} kullanılan
                  </p>
                </div>

                {/* Sosyal İzin Durumu */}
                <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-purple-600" />
                    <h4 className="font-semibold text-sm text-purple-800">👨‍👩‍👧‍👦 Sosyal İzin</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${
                      (12 - socialUsedDays) <= 2
                        ? 'text-red-600'
                        : 'text-purple-700'
                    }`}>
                      {12 - socialUsedDays}
                    </span>
                    <span className="text-xs text-purple-600">gün kalan</span>
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1">
                    12 toplam • {socialUsedDays} kullanılan
                  </p>
                </div>

                {/* Rapor Durumu */}
                <div className="p-3 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg border border-pink-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="h-4 w-4 text-pink-600" />
                    <h4 className="font-semibold text-sm text-pink-800">🏥 Rapor</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${
                      (40 - (personnel.used_report_days || 0)) <= 5 
                        ? 'text-red-600' 
                        : 'text-pink-700'
                    }`}>
                      {40 - (personnel.used_report_days || 0)}
                    </span>
                    <span className="text-xs text-pink-600">gün kalan</span>
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1">
                    40 toplam • {personnel.used_report_days || 0} kullanılan
                  </p>
                </div>

                {/* Diğer İzin Durumu */}
                <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4 text-gray-600" />
                    <h4 className="font-semibold text-sm text-gray-800">📋 Diğer</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-700">
                      {thisYearOtherCount}
                    </span>
                    <span className="text-xs text-gray-600">adet (bu yıl)</span>
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1">
                    Bu yıl toplam diğer izin sayısı
                  </p>
                </div>
              </div>

              {/* Bu Yıl İstatistikleri */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <p className="text-2xl font-bold text-red-600">{thisYearRaporDays}</p>
                  <p className="text-xs text-red-700">🏥 Bu Yıl Rapor Günü</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                  <p className="text-2xl font-bold text-purple-600">{thisYearMaterialCount}</p>
                  <p className="text-xs text-purple-700">📦 Bu Yıl Malzeme Talebi</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-center">
                  <p className="text-2xl font-bold text-orange-600">{totalMaterialsReceived}</p>
                  <p className="text-xs text-orange-700">✅ Toplam Alınan Malzeme</p>
                </div>
                <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 text-center">
                  <p className="text-2xl font-bold text-cyan-600">{assignedParks.length}</p>
                  <p className="text-xs text-cyan-700">🌳 Sorumlu Park</p>
                </div>
              </div>

              {/* Amir Zinciri (dinamik — parent_personnel_id'den yukarı) */}
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-indigo-600" />
                  <h4 className="font-semibold text-indigo-800">Amir Zinciri</h4>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {parentChain.length === 0 ? (
                    <div className="text-sm text-indigo-600 bg-white px-3 py-2 rounded-lg border">
                      Üst amir bulunmuyor — hiyerarşi kökü
                    </div>
                  ) : parentChain.map((node, idx) => (
                    <React.Fragment key={node.id}>
                      {idx > 0 && <span className="text-indigo-400 text-lg">→</span>}
                      <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border shadow-sm">
                        <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-sm font-bold">
                          {node.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {node.role_display_name || node.role_slug}
                          </p>
                          <p className="text-sm font-medium">{node.name}</p>
                        </div>
                        {node.assignment_type === 'GECICI' && (
                          <Badge className="bg-yellow-500 text-white text-[10px]">Geçici</Badge>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* İZİNLER SEKMESİ */}
            <TabsContent value="leaves" className="space-y-4">
              {/* İzin Türü Filtre Kartları - Kalan gün bilgisiyle */}
              <div className="grid grid-cols-5 gap-2">
                <div 
                  onClick={() => setLeaveTypeFilter('all')}
                  className={`p-2 rounded-lg border cursor-pointer transition-all ${
                    leaveTypeFilter === 'all' 
                      ? 'bg-gray-100 border-gray-400 ring-2 ring-gray-300' 
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <p className="text-lg font-bold text-gray-700">{personnelLeaves.length}</p>
                  <p className="text-[10px] text-gray-600">Tümü</p>
                </div>
                <div 
                  onClick={() => setLeaveTypeFilter('Yıllık İzin')}
                  className={`p-2 rounded-lg border cursor-pointer transition-all ${
                    leaveTypeFilter === 'Yıllık İzin' 
                      ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-300' 
                      : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                  }`}
                >
                  <p className="text-lg font-bold text-blue-700">
                    {personnelLeaves.filter(l => l.reason_type === 'Yıllık İzin').length}
                  </p>
                  <p className="text-[10px] text-blue-600">🏖️ Yıllık İzin</p>
                  <p className="text-[10px] text-green-600 font-semibold">
                    Kalan: {(personnel.annual_leave_days || 20) - (personnel.used_leave_days || 0)}/{personnel.annual_leave_days || 20} gün
                  </p>
                </div>
                <div 
                  onClick={() => setLeaveTypeFilter('Sosyal İzin')}
                  className={`p-2 rounded-lg border cursor-pointer transition-all ${
                    leaveTypeFilter === 'Sosyal İzin' 
                      ? 'bg-purple-100 border-purple-400 ring-2 ring-purple-300' 
                      : 'bg-purple-50 border-purple-200 hover:bg-purple-100'
                  }`}
                >
                  <p className="text-lg font-bold text-purple-700">
                    {personnelLeaves.filter(l => l.reason_type === 'Sosyal İzin').length}
                  </p>
                  <p className="text-[10px] text-purple-600">👨‍👩‍👧‍👦 Sosyal İzin</p>
                  <p className="text-[10px] text-green-600 font-semibold">
                    Kalan: {12 - socialUsedDays}/12 gün
                  </p>
                </div>
                <div 
                  onClick={() => setLeaveTypeFilter('Rapor')}
                  className={`p-2 rounded-lg border cursor-pointer transition-all ${
                    leaveTypeFilter === 'Rapor' 
                      ? 'bg-pink-100 border-pink-400 ring-2 ring-pink-300' 
                      : 'bg-pink-50 border-pink-200 hover:bg-pink-100'
                  }`}
                >
                  <p className="text-lg font-bold text-pink-700">
                    {personnelLeaves.filter(l => l.reason_type === 'Rapor').length}
                  </p>
                  <p className="text-[10px] text-pink-600">🏥 Rapor</p>
                  <p className="text-[10px] text-green-600 font-semibold">
                    Kalan: {40 - (personnel.used_report_days || 0)}/40 gün
                  </p>
                </div>
                <div 
                  onClick={() => setLeaveTypeFilter('Diğer')}
                  className={`p-2 rounded-lg border cursor-pointer transition-all ${
                    leaveTypeFilter === 'Diğer' 
                      ? 'bg-gray-200 border-gray-400 ring-2 ring-gray-300' 
                      : 'bg-gray-100 border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  <p className="text-lg font-bold text-gray-700">
                    {personnelLeaves.filter(l => l.reason_type === 'Diğer').length}
                  </p>
                  <p className="text-[10px] text-gray-600">📋 Diğer</p>
                  <p className="text-[10px] text-gray-600 font-semibold">
                    Bu yıl: {thisYearOtherCount}
                  </p>
                </div>
              </div>

              {/* Filtrelenmiş İzin Listesi */}
              {(() => {
                const filteredLeaves = leaveTypeFilter === 'all' 
                  ? personnelLeaves 
                  : personnelLeaves.filter(l => l.reason_type === leaveTypeFilter);
                
                if (filteredLeaves.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>{leaveTypeFilter === 'all' ? 'Henüz izin talebi bulunmuyor' : `${leaveTypeFilter} talebi bulunmuyor`}</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {filteredLeaves
                      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                      .map((leave) => {
                        const startDate = new Date(leave.start_date);
                        const endDate = new Date(leave.end_date);
                        const dayCount = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                        
                        const typeColors = {
                          'Yıllık İzin': { border: 'border-l-blue-500', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700' },
                          'Sosyal İzin': { border: 'border-l-purple-500', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700' },
                          'Rapor': { border: 'border-l-pink-500', bg: 'bg-pink-50', badge: 'bg-pink-100 text-pink-700' },
                          'Diğer': { border: 'border-l-gray-500', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-700' }
                        };
                        const colors = typeColors[leave.reason_type] || typeColors['Diğer'];
                        
                        return (
                          <div key={leave.id} className={`p-3 rounded-lg border-l-4 ${colors.border} ${colors.bg}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors.badge}`}>
                                  {leave.reason_type}
                                </span>
                                <Badge className={
                                  leave.status === 'approved' ? 'bg-green-600 text-xs' :
                                  leave.status === 'rejected' ? 'bg-red-600 text-xs' :
                                  'bg-yellow-600 text-xs'
                                }>
                                  {leave.status === 'approved' ? '✓ Onaylı' :
                                   leave.status === 'rejected' ? '✕ Ret' : '⏳ Bekliyor'}
                                </Badge>
                              </div>
                              <span className="text-sm font-medium text-gray-700">{dayCount} gün</span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {startDate.toLocaleDateString('tr-TR')} - {endDate.toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                );
              })()}
            </TabsContent>

            {/* MALZEMELER SEKMESİ */}
            <TabsContent value="materials" className="space-y-4">
              {personnelMaterials.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                    <p className="text-xl font-bold text-purple-600">{personnelMaterials.length}</p>
                    <p className="text-xs text-purple-700">Toplam Talep</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                    <p className="text-xl font-bold text-green-600">
                      {personnelMaterials.filter(m => m.status === 'delivered').length}
                    </p>
                    <p className="text-xs text-green-700">Teslim Edilen</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                    <p className="text-xl font-bold text-yellow-600">
                      {personnelMaterials.filter(m => m.status === 'pending' || m.status === 'approved').length}
                    </p>
                    <p className="text-xs text-yellow-700">Bekleyen</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                    <p className="text-xl font-bold text-blue-600">{totalMaterialsReceived}</p>
                    <p className="text-xs text-blue-700">Toplam Adet</p>
                  </div>
                </div>
              )}

              {personnelMaterials.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                  <Package className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg">Henüz malzeme talebi bulunmuyor</p>
                </div>
              ) : (
                <Accordion type="multiple" className="space-y-2">
                  {materialTypes.map(materialType => {
                    const materials = groupedMaterials[materialType];
                    const delivered = materials.filter(m => m.status === 'delivered').length;
                    const pending = materials.filter(m => m.status === 'pending' || m.status === 'approved').length;
                    const totalQuantity = materials.reduce((sum, m) => sum + m.quantity, 0);
                    const deliveredQuantity = materials
                      .filter(m => m.status === 'delivered')
                      .reduce((sum, m) => sum + m.quantity, 0);
                    
                    return (
                      <AccordionItem key={materialType} value={materialType} className="border rounded-lg overflow-hidden border-purple-300">
                        <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 bg-gradient-to-r from-purple-50 to-purple-100">
                          <div className="flex items-center justify-between w-full pr-4">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{getMaterialIcon(materialType)}</span>
                              <div className="text-left">
                                <h4 className="font-semibold text-base">{materialType}</h4>
                                <p className="text-xs text-gray-600">
                                  {totalQuantity} adet talep • {deliveredQuantity} adet alındı
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {delivered > 0 && (
                                <Badge variant="outline" className="bg-green-50 border-green-300 text-green-700 text-xs">
                                  ✓ {delivered} Teslim
                                </Badge>
                              )}
                              {pending > 0 && (
                                <Badge variant="outline" className="bg-yellow-50 border-yellow-300 text-yellow-700 text-xs">
                                  ⏳ {pending} Bekliyor
                                </Badge>
                              )}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-2 mt-2">
                            {materials
                              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                              .map((material) => (
                                <div key={material.id} className={`p-3 rounded-lg border-l-4 ${
                                  material.status === 'delivered' ? 'bg-green-50 border-green-500' :
                                  material.status === 'rejected' ? 'bg-red-50 border-red-500' :
                                  'bg-yellow-50 border-yellow-500'
                                }`}>
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <Badge className={
                                      material.status === 'delivered' ? 'bg-green-600' : 
                                      material.status === 'rejected' ? 'bg-red-600' :
                                      'bg-yellow-600'
                                    }>
                                      {material.status === 'delivered' ? '✓ Teslim Edildi' : 
                                       material.status === 'rejected' ? '✕ Reddedildi' :
                                       '⏳ Bekliyor'}
                                    </Badge>
                                    <span className="text-sm font-bold text-gray-900 bg-white px-2 py-0.5 rounded border">
                                      {material.quantity} Adet
                                    </span>
                                    <span className="text-xs text-gray-600">
                                      📅 {new Date(material.created_at).toLocaleDateString('tr-TR')}
                                    </span>
                                  </div>
                                  {material.delivered_at && (
                                    <p className="text-xs text-green-600">
                                      <CheckCircle className="inline h-3 w-3 mr-1" />
                                      Teslim: {new Date(material.delivered_at).toLocaleDateString('tr-TR')}
                                    </p>
                                  )}
                                </div>
                              ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </TabsContent>

            {/* PARKLAR SEKMESİ */}
            <TabsContent value="parks" className="space-y-4">
              {assignedParks.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                  <TreePine className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg">Henüz sorumlu park atanmamış</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {assignedParks.map(park => {
                    // Bu parka atanan park görevlilerini bul (paylaşımlı park tespiti)
                    const parkGorevlileri = (park.assigned_personnel_ids || [])
                      .map(pid => allPersonnel.find(p => p.id === pid))
                      .filter(Boolean)
                      .filter(p => p.role === 'park_gorevlisi')
                      .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr"));
                    const coWorkers = parkGorevlileri.filter(
                      p => p.id !== personnel.id && p.name !== personnel.name
                    );
                    const totalGorevli = parkGorevlileri.length;
                    const isShared = coWorkers.length > 0;
                    return (
                    <div key={park.id} className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg border border-green-200 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-lg">
                          🌳
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-green-800">{park.name}</h4>
                            {isShared && (
                              <Badge className="text-xs bg-purple-500 text-white">
                                👥 Paylaşımlı · {totalGorevli} görevli
                              </Badge>
                            )}
                          </div>
                          {park.address && (
                            <p className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" /> {park.address}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {park.total_area && (
                              <Badge variant="outline" className="text-xs bg-white">
                                📐 {park.total_area} m²
                              </Badge>
                            )}
                            {park.grass_area && (
                              <Badge variant="outline" className="text-xs bg-white">
                                🌿 Çim: {park.grass_area} m²
                              </Badge>
                            )}
                          </div>
                          {isShared && (
                            <p className="text-xs text-purple-700 mt-2 flex items-start gap-1">
                              <span className="font-medium whitespace-nowrap">Birlikte:</span>
                              <span>{coWorkers.map(p => p.name).join(', ')}</span>
                            </p>
                          )}
                          {isShared && (
                            <p className="text-[11px] text-gray-500 italic mt-1">
                              Bu park {totalGorevli} görevli tarafından ortak yürütülmektedir; yukarıdaki alanlar parkın tamamına aittir.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* VEKALET SEKMESİ */}
            <TabsContent value="substitutions" className="space-y-4">
              <SubstitutionTab 
                personnel={personnel} 
                allPersonnel={allPersonnel}
              />
            </TabsContent>

            {/* DÜZENLE SEKMESİ */}
            <TabsContent value="edit" className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Personel Bilgilerini Düzenle
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name">Ad Soyad</Label>
                    <Input
                      id="edit-name"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-phone">Telefon</Label>
                    {!canEditPhone && (
                      <p className="text-xs text-muted-foreground mb-1">
                        Yalnızca müdür ve süper yönetici düzenleyebilir (05XX XXX XX XX).
                      </p>
                    )}
                    <Input
                      id="edit-phone"
                      type="tel"
                      inputMode="numeric"
                      value={formatTrCepDisplay(editForm.phone)}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          phone: normalizeTrCepToLocal11(e.target.value),
                        })
                      }
                      placeholder="0532 123 45 67"
                      disabled={!canEditPhone}
                      className={!canEditPhone ? "bg-gray-100" : ""}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-email">E-posta</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    />
                  </div>
                  
                </div>

                {/* Rol ve Hiyerarşi — inline edit, kullanıcının yetkisi varsa */}
                <div className="mt-6 pt-4 border-t border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" />
                    Rol ve Hiyerarşi
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Rol *</Label>
                      <SearchableSelect
                        value={editForm.role_id || ''}
                        onChange={(roleIdValue) => {
                          const selected = getRoleById(roleIdValue);
                          const newSlug = selected?.slug || '';
                          setEditForm((prev) => ({
                            ...prev,
                            role_id: roleIdValue,
                            role: newSlug,
                            parent_personnel_id: null,
                            assignment_type: 'NORMAL',
                          }));
                        }}
                        disabled={!canEditRole}
                        showAllOption={false}
                        placeholder="Rol seçin"
                        searchPlaceholder="Rol ara..."
                        emptyText="Aktif rol yok"
                        options={hierarchyRoles.map((r) => ({ id: r.id, name: r.display_name || r.slug }))}
                      />
                    </div>

                    {/* Özel Rol Ata — yalnızca etiket */}
                    <div className="flex flex-col gap-2">
                      <Label className="opacity-0 hidden md:block">&nbsp;</Label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2 border-purple-300 text-purple-800 hover:bg-purple-50"
                          onClick={() => {
                            setCustomRoleDraft({
                              name: editForm.custom_role_name || '',
                              description: editForm.custom_role_description || '',
                            });
                            setCustomRoleDialogOpen(true);
                          }}
                          disabled={!canEditRole}
                        >
                          <Sparkles className="h-4 w-4" />
                          Özel Rol Ata
                        </Button>
                        {editForm.custom_role_name && (
                          <Badge className="gap-1 bg-purple-100 text-purple-900 border border-purple-200">
                            {editForm.custom_role_name}
                            {canEditRole && (
                              <button
                                type="button"
                                onClick={() => setEditForm((prev) => ({
                                  ...prev,
                                  custom_role_name: '',
                                  custom_role_description: '',
                                }))}
                                className="ml-1 opacity-60 hover:opacity-100"
                                aria-label="Özel rolü kaldır"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </Badge>
                        )}
                      </div>
                      {editForm.custom_role_description && (
                        <p className="text-[11px] text-muted-foreground line-clamp-2">
                          {editForm.custom_role_description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Bağlı Olduğu Amir</Label>
                      <SupervisorPicker
                        value={editForm.parent_personnel_id || null}
                        onChange={(nextParent) => {
                          setEditForm((prev) => ({
                            ...prev,
                            parent_personnel_id: nextParent,
                            assignment_type: nextParent ? (prev.assignment_type || 'NORMAL') : 'NORMAL',
                          }));
                        }}
                        eligibleSupervisors={eligibleSupervisors}
                        getRoleById={getRoleById}
                        getRoleBySlug={getRoleBySlug}
                        getRoleLabel={getRoleLabel}
                        disabled={!canEditRole || supervisorMode !== 'ready'}
                        placeholder={supervisorPlaceholder}
                      />
                      {supervisorHint && (
                        <p className={`mt-1 text-xs ${supervisorMode === 'no-role' ? 'text-amber-600' : 'text-slate-500'}`}>
                          {supervisorHint}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Atama Tipi</Label>
                      <SearchableSelect
                        value={editForm.assignment_type || 'NORMAL'}
                        onChange={(value) => setEditForm((prev) => ({ ...prev, assignment_type: value }))}
                        disabled={!canEditRole || !editForm.parent_personnel_id}
                        showAllOption={false}
                        placeholder="Atama tipi seçin"
                        searchPlaceholder="Atama tipi ara..."
                        options={[
                          { id: "NORMAL", name: "Normal" },
                          { id: "GECICI", name: "Geçici" },
                        ]}
                      />
                    </div>
                  </div>

                  {/* Ekip Atamaları — role bağlı (Migration 0066) */}
                  {editForm.role && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <TeamPicker
                        roleSlug={editForm.role}
                        primaryTeamId={editForm.primary_team_id || null}
                        secondaryTeamId={editForm.secondary_team_id || null}
                        onChange={({ primary, secondary }) =>
                          setEditForm((prev) => ({
                            ...prev,
                            primary_team_id: primary,
                            secondary_team_id: secondary,
                          }))
                        }
                        disabled={!canEditRole}
                      />
                    </div>
                  )}
                </div>

                {/* İzin Bilgileri */}
                <div className="mt-6 pt-4 border-t border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    İzin Bilgileri
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <DatePicker
                        id="edit-hire-date"
                        label="İşe Giriş Tarihi"
                        value={editForm.hire_date}
                        onChange={(value) => setEditForm({...editForm, hire_date: value})}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-annual-leave">Yıllık İzin Hakkı (Gün)</Label>
                      <Input
                        id="edit-annual-leave"
                        type="number"
                        min="0"
                        max="60"
                        value={editForm.annual_leave_days}
                        onChange={(e) => setEditForm({...editForm, annual_leave_days: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-used-leave">Kullanılan İzin (Gün)</Label>
                      <Input
                        id="edit-used-leave"
                        type="number"
                        min="0"
                        value={editForm.used_leave_days}
                        onChange={(e) => setEditForm({...editForm, used_leave_days: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  
                  {/* Kalan İzin Gösterimi */}
                  <div className="mt-3 p-3 bg-white rounded-lg border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Kalan Yıllık İzin:</span>
                      <Badge 
                        className={`text-sm ${
                          (editForm.annual_leave_days - editForm.used_leave_days) <= 5 
                            ? 'bg-red-500' 
                            : 'bg-green-500'
                        }`}
                      >
                        {editForm.annual_leave_days - editForm.used_leave_days} gün
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Durum */}
                <div className="mt-6 pt-4 border-t border-blue-200">
                  <Label htmlFor="edit-status">Durum</Label>
                  <SearchableSelect
                    value={editForm.status}
                    onChange={(value) => setEditForm({...editForm, status: value})}
                    showAllOption={false}
                    placeholder="Durum seçin"
                    searchPlaceholder="Durum ara..."
                    className="w-48 mt-1"
                    options={[
                      { id: "active", name: "Aktif" },
                      { id: "inactive", name: "Pasif" },
                    ]}
                  />
                </div>
                
                {/* Kaydet Butonu */}
                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Değişiklikleri Kaydet
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* İleri Düzey Düzenleme Butonu */}
              <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-orange-800">İleri Düzey Düzenleme</h4>
                    <p className="text-xs text-orange-600 mt-1">
                      Rol, bölge, park ataması, hiyerarşi ve çalışma saatleri ayarları
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="bg-orange-100 hover:bg-orange-200 border-orange-300 text-orange-800"
                    onClick={() => {
                      if (onOpenAdvancedEdit) {
                        onClose(false);
                        onOpenAdvancedEdit(personnel);
                      }
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    İleri Düzey Düzenle
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
    </DraggableModal>

    <Dialog
      open={setPasswordOpen}
      onOpenChange={(open) => {
        setSetPasswordOpen(open);
        if (!open) {
          setNewPassword("");
          setNewPasswordConfirm("");
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Şifre ata / değiştir</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">Bu personel için giriş şifresi (en az 4 karakter). İki alana aynı şifreyi yazın.</p>
        <Input
          type="password"
          placeholder="Yeni şifre"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          minLength={4}
          autoComplete="new-password"
          className="mt-2"
        />
        <Input
          type="password"
          placeholder="Yeni şifre (tekrar)"
          value={newPasswordConfirm}
          onChange={(e) => setNewPasswordConfirm(e.target.value)}
          minLength={4}
          autoComplete="new-password"
          className="mt-2"
        />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => { setSetPasswordOpen(false); setNewPassword(""); setNewPasswordConfirm(""); }}>
            İptal
          </Button>
          <Button
            onClick={handleSetPassword}
            disabled={
              !newPassword.trim() ||
              newPassword.length < 4 ||
              newPassword !== newPasswordConfirm ||
              setPasswordLoading
            }
          >
            {setPasswordLoading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Özel Rol Ata — yalnızca etiket; sistem rolleri ve hiyerarşi etkilenmez */}
    <Dialog open={customRoleDialogOpen} onOpenChange={setCustomRoleDialogOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Özel Rol Ata
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Bu rol sistem rolleri arasına eklenmez; yalnızca bu personelin
            kartında etiket olarak görüntülenir. Yetki ve hiyerarşi değişmez.
          </p>
          <div>
            <Label htmlFor="details-custom-role-name">Özel Rol Adı *</Label>
            <Input
              id="details-custom-role-name"
              value={customRoleDraft.name}
              onChange={(e) => setCustomRoleDraft((p) => ({ ...p, name: e.target.value }))}
              placeholder="Örn: Kent Tarımı Sorumlusu"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="details-custom-role-desc">Açıklama (opsiyonel)</Label>
            <Textarea
              id="details-custom-role-desc"
              value={customRoleDraft.description}
              onChange={(e) => setCustomRoleDraft((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              placeholder="Bu rolün kapsamını açıklayabilirsiniz..."
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => setCustomRoleDialogOpen(false)}>
            Vazgeç
          </Button>
          <Button
            onClick={() => {
              const name = customRoleDraft.name.trim();
              if (!name) {
                toast.error("Özel rol adı boş olamaz");
                return;
              }
              setEditForm((prev) => ({
                ...prev,
                custom_role_name: name,
                custom_role_description: customRoleDraft.description.trim() || '',
              }));
              setCustomRoleDialogOpen(false);
            }}
          >
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
}
